// Manage agents on an existing appointment.
// Shows current assignments + add/remove controls. Roles: primary / secondary / call_center.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserPlus, X, Crown, Star, Phone, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar } from '@/components/ui/avatar';
import { useAgents } from '@/hooks/data/useAgents';
import {
  type AppointmentAgentRow,
  useAddAppointmentAgent,
  useAppointmentAgents,
  useRemoveAppointmentAgent,
} from '@/hooks/data/useAppointments';

interface Props {
  appointmentId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const ROLE_LABEL: Record<AppointmentAgentRow['role'], string> = {
  primary: 'Hauptmakler',
  secondary: 'Co-Makler',
  call_center: 'Call Center',
};

const ROLE_ICON: Record<AppointmentAgentRow['role'], React.ReactNode> = {
  primary: <Crown className="h-3 w-3" />,
  secondary: <Star className="h-3 w-3" />,
  call_center: <Phone className="h-3 w-3" />,
};

const ROLE_PILL: Record<AppointmentAgentRow['role'], string> = {
  primary: 'fm-pill-gold',
  secondary: 'fm-pill-info',
  call_center: 'fm-pill-neutral',
};

export function MultiAgentDialog({ appointmentId, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { data: assigned = [] } = useAppointmentAgents(open ? appointmentId : undefined);
  const { data: agents = [] } = useAgents();
  const add = useAddAppointmentAgent(appointmentId);
  const remove = useRemoveAppointmentAgent(appointmentId);

  const assignedIds = useMemo(
    () => new Set(assigned.map((a) => a.agent_profile_id)),
    [assigned],
  );
  const available = agents.filter(
    (a) => a.role !== 'admin' && a.status === 'active' && !assignedIds.has(a.id),
  );

  const agentMap = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  const [pendingAgent, setPendingAgent] = useState('');
  const [pendingRole, setPendingRole] = useState<AppointmentAgentRow['role']>('secondary');
  const [agentQuery, setAgentQuery] = useState('');

  const filteredAvailable = useMemo(() => {
    const q = agentQuery.trim().toLowerCase();
    if (!q) return available;
    return available.filter(
      (a) =>
        (a.full_name ?? '').toLowerCase().includes(q) ||
        (a.agent_id ?? '').toLowerCase().includes(q) ||
        (a.role ?? '').toLowerCase().includes(q),
    );
  }, [available, agentQuery]);

  const onAdd = async () => {
    if (!pendingAgent) return;
    try {
      await add.mutateAsync({ agentProfileId: pendingAgent, role: pendingRole });
      setPendingAgent('');
      toast.success(t('appointments.agents.added', 'Agent hinzugefügt.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onRemove = async (agentProfileId: string) => {
    try {
      await remove.mutateAsync(agentProfileId);
      toast.success(t('appointments.agents.removed', 'Agent entfernt.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <UserPlus className="h-4 w-4" />
            {t('appointments.agents.title', 'Termin-Agenten verwalten')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current assignments */}
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-ink-3">
              {t('appointments.agents.assigned', 'Zugewiesen')} ({assigned.length})
            </p>
            {assigned.length === 0 ? (
              <p className="rounded-md border border-dashed border-line-strong px-3 py-3 text-center text-xs text-ink-3">
                {t('appointments.agents.empty', 'Noch keine Agenten zugewiesen.')}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {assigned.map((row) => {
                  const a = agentMap.get(row.agent_profile_id);
                  const name = a?.full_name ?? row.agent_profile_id;
                  return (
                    <li
                      key={row.id}
                      className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2"
                    >
                      <Avatar name={name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{name}</p>
                        {a?.agent_id && (
                          <p className="font-mono text-[11px] text-ink-3">{a.agent_id}</p>
                        )}
                      </div>
                      <span className={`fm-pill ${ROLE_PILL[row.role]}`}>
                        {ROLE_ICON[row.role]}
                        {ROLE_LABEL[row.role]}
                      </span>
                      <button
                        type="button"
                        onClick={() => onRemove(row.agent_profile_id)}
                        disabled={remove.isPending}
                        title={t('common.remove', 'Entfernen')}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))] disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Add new */}
          <div className="rounded-xl border border-line bg-card p-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-ink-3">
              {t('appointments.agents.add', 'Hinzufügen')}
            </p>
            <input
              type="text"
              value={agentQuery}
              onChange={(e) => setAgentQuery(e.target.value)}
              placeholder={t('appointments.agents.search', 'Agent suchen (Name oder ID)')}
              className="mb-2 w-full rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
            <div className="mb-2 max-h-48 overflow-y-auto rounded-md border border-line bg-paper-2">
              {filteredAvailable.length === 0 ? (
                <p className="px-3 py-3 text-center text-xs text-ink-3">
                  {t('appointments.agents.no_match', 'Keine Treffer')}
                </p>
              ) : (
                <ul>
                  {filteredAvailable.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => setPendingAgent(a.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-start text-sm transition-colors ${
                          pendingAgent === a.id ? 'bg-card text-ink' : 'text-ink-2 hover:bg-card'
                        }`}
                      >
                        <Avatar name={a.full_name} size="sm" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{a.full_name}</p>
                          <p className="font-mono text-[10px] text-ink-3">
                            {a.agent_id} · {a.role}
                          </p>
                        </div>
                        {pendingAgent === a.id && <Check className="h-4 w-4 text-[hsl(var(--positive))]" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                value={pendingRole}
                onChange={(e) => setPendingRole(e.target.value as AppointmentAgentRow['role'])}
                className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 sm:flex-1"
              >
                <option value="primary">{t('appointments.agents.role_primary', 'Hauptmakler')}</option>
                <option value="secondary">{t('appointments.agents.role_secondary', 'Co-Makler')}</option>
                <option value="call_center">{t('appointments.agents.role_call_center', 'Call Center')}</option>
              </select>
              <button
                type="button"
                onClick={onAdd}
                disabled={!pendingAgent || add.isPending}
                className="inline-flex items-center justify-center gap-1 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-50"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {t('common.add', 'Hinzufügen')}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
